import React, { useEffect, useRef, useState } from "react";
import {
  CheckCircle2,
  CircleAlert,
  HelpCircle,
  LockKeyhole,
  LoaderCircle,
  RotateCcw,
  ScanFace,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  submitFBConversionEvent,
  submitGGconversionEvent,
  submitTikTokConversionEvent,
} from "@/api/FacebookAPI";
import { countPixelEvent } from "@/api/CountPixelEventAPI";
import { RecordingController } from "@/features/kyc/document-camera/RecordingController";
import { getFaceLandmarkerClient } from "@/features/kyc/liveness/FaceLandmarkerClient";
import { getFacePositionValidation } from "@/features/kyc/liveness/FacePose";
import { LivenessChallengeMachine } from "@/features/kyc/liveness/LivenessChallengeMachine";
import { LivenessConsoleLogger } from "@/features/kyc/liveness/LivenessConsoleLogger";
import { LIVENESS_CONFIG } from "@/features/kyc/liveness/livenessConfig";
import { SegmentedFaceGuide } from "@/features/kyc/liveness/SegmentedFaceGuide";
import { generateEventId } from "@/utils/RandomGeneratorUtils";
import { uploadFaceVideo } from "@/utils/UploadFile";

const MAX_CONSECUTIVE_DETECTION_ERRORS = 3;

const formatFileSize = (bytes) => {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KiB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MiB`;
};

const trackFaceCheckStarted = (orderDetail = {}) => {
  const eventId = generateEventId("AddPaymentInfo", "job");
  const eventParams = { value: 1 };
  const userData = {
    em: orderDetail.email,
    ph: orderDetail.phone,
    fn: orderDetail.firstName,
    ln: orderDetail.lastName,
    ct: orderDetail.city,
    st: orderDetail.state,
    zp: orderDetail.zipcode,
    country: orderDetail.country,
  };

  submitFBConversionEvent(
    "AddPaymentInfo",
    eventParams,
    orderDetail.email ? userData : null,
    eventId,
    null,
  );
  countPixelEvent("Add Payment Info", eventId);
  submitGGconversionEvent("add_payment_info", {
    currency: "USD",
    value: 1,
    items: [
      {
        item_id: `KYC_${orderDetail.id}`,
        item_name: "KYC Application",
        item_category: "Application",
      },
    ],
    application_id: `KYC_${orderDetail.firstName}_${orderDetail.id}`,
    model: "job",
    country: orderDetail.country,
  });
  submitTikTokConversionEvent("AddPaymentInfo", {
    currency: "USD",
    value: 1,
  });
};

const getCameraErrorKey = (error) =>
  ["NotAllowedError", "SecurityError"].includes(error?.name)
    ? "kyc.cameraDenied"
    : "kyc.liveness.detection.unavailable";

const getTechnicalErrorDetails = (error, stage) => {
  if (!error && !stage) return {};

  return {
    ...(stage ? { 失败阶段: stage } : {}),
    ...(error
      ? {
          原始错误名称: error.name || "Error",
          原始错误信息: error.message || String(error),
        }
      : {}),
  };
};

const createInitialChallenge = () => new LivenessChallengeMachine().reset();

export function FaceCapture({
  isFirstSubmitFace = 0,
  orderDetail,
  ssn,
  onComplete,
  clearError,
  autoStart = true,
}) {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const animationFrameRef = useRef(null);
  const sessionIdRef = useRef(0);
  const sessionActiveRef = useRef(false);
  const startingRef = useRef(false);
  const finishingRef = useRef(false);
  const lastDetectionAtRef = useRef(0);
  const detectionErrorsRef = useRef(0);
  const uploadAbortControllerRef = useRef(null);
  const machineRef = useRef(new LivenessChallengeMachine());
  const loggerRef = useRef(new LivenessConsoleLogger());
  const callbacksRef = useRef({ onComplete, clearError });
  const startSessionRef = useRef(null);
  const [status, setStatus] = useState(autoStart ? "initializing" : "idle");
  const [challenge, setChallenge] = useState(createInitialChallenge);
  const [error, setError] = useState("");

  // 摄像头、检测器、录制器和上传都是异步任务，执行时间可能超过一次
  // React 渲染周期。这里用 ref 保存同一次会话和最新的父组件回调。
  callbacksRef.current = { onComplete, clearError };

  const stopDetectionLoop = () => {
    sessionActiveRef.current = false;
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const cancelRecording = () => {
    recorderRef.current?.cancel();
    recorderRef.current = null;
  };

  const cancelUpload = () => {
    if (uploadAbortControllerRef.current) {
      loggerRef.current.info("分片上传已取消");
    }
    uploadAbortControllerRef.current?.abort();
    uploadAbortControllerRef.current = null;
  };

  const failSession = (messageKey, technicalError, stage) => {
    stopDetectionLoop();
    cancelRecording();
    cancelUpload();
    stopCamera();
    startingRef.current = false;
    finishingRef.current = false;
    const message = t(messageKey);
    loggerRef.current.error("检测会话结果：失败", {
      失败原因: message,
      ...getTechnicalErrorDetails(technicalError, stage),
    });
    setError(message);
    setStatus("error");
  };

  const finishRecording = () => {
    if (finishingRef.current) return;
    finishingRef.current = true;
    stopDetectionLoop();
    loggerRef.current.info("所有动作已通过，正在结束后台录制");
    setStatus("complete");

    if (!recorderRef.current?.stop()) {
      failSession(
        "kyc.liveness.detection.unavailable",
        new Error("MediaRecorder could not stop the active recording."),
        "结束后台录制",
      );
    }
  };

  const captureFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState < 2 || !video.videoWidth) {
      return null;
    }

    // 只缩小送入检测器的画面。MediaRecorder 仍然录制原始摄像头流，
    // 因此可以降低检测开销，同时不影响最终上传的视频质量。
    const width = Math.min(
      LIVENESS_CONFIG.detectorSampleWidth,
      video.videoWidth,
    );
    const height = Math.max(
      1,
      Math.round(width * (video.videoHeight / video.videoWidth)),
    );
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    context.drawImage(video, 0, 0, width, height);
    return context.getImageData(0, 0, width, height);
  };

  const startDetectionLoop = (sessionId) => {
    // 每次等待 Worker 返回结果后，才安排下一次有效检测，避免性能较低
    // 的手机不断积压尚未处理的人脸推理任务。
    const detectFrame = async (timestamp) => {
      if (!sessionActiveRef.current || sessionIdRef.current !== sessionId) {
        return;
      }

      if (
        timestamp - lastDetectionAtRef.current <
        LIVENESS_CONFIG.detectionIntervalMs
      ) {
        animationFrameRef.current = requestAnimationFrame(detectFrame);
        return;
      }
      lastDetectionAtRef.current = timestamp;

      try {
        const imageData = captureFrame();
        if (!imageData) {
          animationFrameRef.current = requestAnimationFrame(detectFrame);
          return;
        }

        const workerPose = await getFaceLandmarkerClient().detect(
          imageData,
          timestamp,
        );
        if (!sessionActiveRef.current || sessionIdRef.current !== sessionId) {
          return;
        }

        // 在主线程再次读取 FacePose.js 的位置参数。开发时修改阈值后，
        // 不必等待已经启动的 MediaPipe Worker 重新初始化才能看到效果。
        const positionValidation = getFacePositionValidation(workerPose.bounds);
        const pose = {
          ...workerPose,
          positionValid: workerPose.faceDetected && positionValidation.valid,
          positionFailures: positionValidation.failures,
        };

        detectionErrorsRef.current = 0;
        const nextChallenge = machineRef.current.update({
          ...pose,
          timestamp,
        });
        loggerRef.current.logSnapshot({
          ...nextChallenge,
          bounds: pose.bounds,
          positionFailures: pose.positionFailures,
        });
        setChallenge(nextChallenge);

        if (nextChallenge.complete) {
          finishRecording();
          return;
        }
        if (nextChallenge.timedOut) {
          failSession("kyc.liveness.detection.timeout");
          return;
        }

        setStatus(nextChallenge.step === 0 ? "positioning" : "detecting");
      } catch (detectionError) {
        detectionErrorsRef.current += 1;
        loggerRef.current.logDetectionError(
          detectionError,
          detectionErrorsRef.current,
          MAX_CONSECUTIVE_DETECTION_ERRORS,
        );
        if (detectionErrorsRef.current >= MAX_CONSECUTIVE_DETECTION_ERRORS) {
          failSession(
            "kyc.liveness.detection.unavailable",
            detectionError,
            "逐帧人脸检测",
          );
          return;
        }
      }

      if (sessionActiveRef.current && sessionIdRef.current === sessionId) {
        animationFrameRef.current = requestAnimationFrame(detectFrame);
      }
    };

    sessionActiveRef.current = true;
    animationFrameRef.current = requestAnimationFrame(detectFrame);
  };

  const handleRecordingComplete = async (blob, sessionId) => {
    recorderRef.current = null;
    stopCamera();
    if (sessionIdRef.current !== sessionId) return;

    loggerRef.current.info("后台录制结果：视频 Blob 已生成", {
      文件大小: formatFileSize(blob.size),
      文件类型: blob.type || "未知",
    });

    // 用户不需要预览或手动上传。MediaRecorder 生成最终 Blob 后，
    // 自动上传并把服务器地址交回 KYC 主流程。
    setStatus("processing");
    const uploadController = new AbortController();
    uploadAbortControllerRef.current = uploadController;
    try {
      const url = await uploadFaceVideo(blob, ssn, {
        signal: uploadController.signal,
        onStart: ({ totalChunks, chunkSize, fileSize }) => {
          loggerRef.current.info("开始分片上传", {
            视频大小: formatFileSize(fileSize),
            分片大小: formatFileSize(chunkSize),
            分片总数: totalChunks,
          });
        },
        onRetry: ({ chunkIndex, attempt, maxAttempts }) => {
          loggerRef.current.warn("视频分片上传失败，准备重试", {
            分片序号: chunkIndex + 1,
            当前尝试: `${attempt}/${maxAttempts}`,
          });
        },
        onProgress: ({ uploadedChunks, totalChunks, progress }) => {
          loggerRef.current.info("视频分片上传结果：成功", {
            上传进度: `${uploadedChunks}/${totalChunks}`,
            完成比例: `${Math.round(progress * 100)}%`,
          });
        },
        onMergeStart: () => {
          loggerRef.current.info("视频分片全部上传完成，开始合并压缩");
        },
      });
      if (!url) throw new Error("视频分片上传或服务端合并未成功。");
      if (sessionIdRef.current !== sessionId) return;

      loggerRef.current.info("视频上传最终结果：分片已合并成功");
      if (isFirstSubmitFace < 1) trackFaceCheckStarted(orderDetail);
      callbacksRef.current.onComplete?.(url);
    } catch (uploadError) {
      loggerRef.current.error("视频上传最终结果：失败", {
        失败原因: uploadError?.message || "未知错误",
      });
      if (sessionIdRef.current === sessionId) {
        failSession("kyc.liveness.detection.uploadFailed");
      }
    } finally {
      if (uploadAbortControllerRef.current === uploadController) {
        uploadAbortControllerRef.current = null;
      }
    }
  };

  const startSession = async () => {
    if (startingRef.current || sessionActiveRef.current) return;

    sessionIdRef.current += 1;
    const sessionId = sessionIdRef.current;
    loggerRef.current.reset(sessionId);
    loggerRef.current.info("开始初始化摄像头和人脸检测模型");
    startingRef.current = true;
    finishingRef.current = false;
    detectionErrorsRef.current = 0;
    cancelUpload();
    lastDetectionAtRef.current = 0;
    callbacksRef.current.clearError?.();
    setError("");
    setStatus("initializing");
    machineRef.current = new LivenessChallengeMachine();
    setChallenge(machineRef.current.reset());

    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      failSession(
        "kyc.liveness.detection.unavailable",
        new Error(
          `Required browser APIs are unavailable: getUserMedia=${Boolean(
            navigator.mediaDevices?.getUserMedia,
          )}, MediaRecorder=${Boolean(window.MediaRecorder)}`,
        ),
        "浏览器能力检查",
      );
      return;
    }

    let startupStage = "摄像头初始化";
    try {
      // 这是正常流程中唯一一次用户操作。取得摄像头权限以后，动作检测、
      // 停止后台录制和上传全部自动执行。
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 720 },
          height: { ideal: 960 },
        },
        audio: false,
      });
      if (sessionIdRef.current !== sessionId) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      loggerRef.current.info("摄像头初始化结果：成功", {
        视频宽度: videoRef.current?.videoWidth || "等待首帧",
        视频高度: videoRef.current?.videoHeight || "等待首帧",
      });

      startupStage = "MediaPipe 人脸模型初始化";
      await getFaceLandmarkerClient().initialize();
      if (sessionIdRef.current !== sessionId) return;
      loggerRef.current.info("MediaPipe 人脸模型初始化结果：成功");

      startupStage = "后台录制初始化";
      const recorder = new RecordingController({
        stream,
        autoStop: false,
        onComplete: (blob) => handleRecordingComplete(blob, sessionId),
        onError: (recordingError) =>
          failSession(
            "kyc.liveness.detection.unavailable",
            recordingError,
            "后台录制",
          ),
      });
      recorderRef.current = recorder;
      if (!recorder.start()) return;
      loggerRef.current.info("后台录制初始化结果：成功");

      startingRef.current = false;
      setStatus("positioning");
      loggerRef.current.info("实时动作检测循环已启动");
      startDetectionLoop(sessionId);
    } catch (startError) {
      if (sessionIdRef.current === sessionId) {
        failSession(
          getCameraErrorKey(startError),
          startError,
          startupStage,
        );
      }
    }
  };

  startSessionRef.current = startSession;

  useEffect(() => {
    if (autoStart) startSessionRef.current?.();

    return () => {
      sessionIdRef.current += 1;
      startingRef.current = false;
      finishingRef.current = false;
      stopDetectionLoop();
      cancelRecording();
      cancelUpload();
      stopCamera();
    };
  }, [autoStart]);

  const promptKey =
    status === "processing"
      ? "processing"
      : status === "complete"
        ? "complete"
        : challenge.prompt || "positioning";
  const isComplete =
    status === "complete" || status === "processing" || challenge.complete;
  const showLiveCamera = ["positioning", "detecting", "complete"].includes(
    status,
  );

  return (
    <div className="face-capture mx-auto w-full max-w-md">
      <div
        className={`face-capture-viewport face-capture-viewport--${status} relative mx-auto w-full overflow-hidden`}
      >
        <video
          ref={videoRef}
          muted
          playsInline
          aria-label={t("kyc.liveness.detection.cameraPreview")}
          className={`h-full w-full scale-x-[-1] object-cover transition-opacity duration-300 ${
            showLiveCamera ? "opacity-100" : "opacity-0"
          }`}
        />

        {showLiveCamera && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <SegmentedFaceGuide challenge={challenge} />
          </div>
        )}

        {showLiveCamera && (
          <div className="pointer-events-none absolute inset-x-0 top-0 flex items-center p-3">
            <span className="inline-flex min-h-8 items-center gap-2 rounded-full border border-white/15 bg-black/55 px-3 text-xs font-semibold text-white shadow-sm backdrop-blur-md">
              {isComplete ? (
                <CheckCircle2 className="size-3.5 text-emerald-300" />
              ) : (
                <span className="size-2 rounded-full bg-emerald-400" />
              )}
              {t(
                `kyc.liveness.detection.${
                  isComplete ? "complete" : "detecting"
                }`,
              )}
            </span>
          </div>
        )}

        {(status === "idle" || status === "initializing") && (
          <div className="face-capture-ready absolute inset-0 flex flex-col items-center justify-center text-center">
            <span className="face-capture-ready-icon" aria-hidden="true">
              {status === "initializing" ? (
                <LoaderCircle className="size-8 animate-spin" />
              ) : (
                <ScanFace className="size-9" strokeWidth={1.8} />
              )}
            </span>

            <p className="face-capture-ready-title">
              {t(
                `kyc.liveness.detection.${
                  status === "initializing" ? "preparing" : "readyTitle"
                }`,
              )}
            </p>
            <p className="face-capture-ready-description">
              {t(
                `kyc.liveness.detection.${
                  status === "initializing"
                    ? "preparingDescription"
                    : "readyDescription"
                }`,
              )}
            </p>

            <span className="face-capture-security">
              <LockKeyhole
                className="size-3.5"
                strokeWidth={2.2}
                aria-hidden="true"
              />
              {t("kyc.portal.secureFooter")}
            </span>
          </div>
        )}

        {(status === "processing" || status === "error") && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-[radial-gradient(circle_at_50%_38%,rgba(20,184,166,0.18),transparent_38%),linear-gradient(180deg,#102235_0%,#07111d_100%)] px-8 text-center text-white">
            <span className="flex size-16 items-center justify-center rounded-full border border-white/15 bg-white/10">
              {status === "processing" ? (
                <LoaderCircle className="size-8 animate-spin text-teal-300" />
              ) : (
                <ScanFace className="size-8 text-amber-300" />
              )}
            </span>
            <p className="mt-4 text-base font-semibold">
              {status === "processing"
                ? t("kyc.liveness.detection.processing")
                : t("kyc.liveness.detection.actionNeeded")}
            </p>
            <p className="mt-1 max-w-xs text-sm leading-5 text-slate-300">
              {status === "processing"
                ? t("kyc.liveness.detection.processingDescription")
                : error}
            </p>
          </div>
        )}

        {showLiveCamera && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent px-5 pb-4 pt-20 text-center text-white">
            <p
              className="mx-auto inline-flex min-h-9 max-w-[90%] items-center justify-center rounded-lg border border-white/10 bg-slate-950/75 px-3 py-2 text-sm font-semibold shadow-lg backdrop-blur-md"
              aria-live="polite"
              aria-atomic="true"
            >
              {t(`kyc.liveness.detection.${promptKey}`)}
            </p>
          </div>
        )}
      </div>

      <canvas ref={canvasRef} className="hidden" />

      {status !== "error" && status !== "processing" && (
        <details className="face-capture-help group mx-auto mt-3 text-center">
          <summary>
            <HelpCircle className="size-4" strokeWidth={2} aria-hidden="true" />
            {t("kyc.liveness.detection.havingTrouble")}
          </summary>
          <p>{t("kyc.liveness.detection.troubleTip")}</p>
        </details>
      )}

      {status === "error" && (
        <div className="mt-3">
          <p
            className="flex items-start justify-center gap-2 rounded-xl border border-red-100 bg-red-50 px-4 py-2 text-sm font-medium leading-5 text-red-700"
            role="alert"
          >
            <CircleAlert
              className="mt-0.5 size-4 shrink-0"
              aria-hidden="true"
            />
            {error}
          </p>
          <button
            type="button"
            onClick={startSession}
            className="btn-primary mt-3 w-full"
          >
            <RotateCcw className="size-4" aria-hidden="true" />
            {t("kyc.liveness.detection.retry")}
          </button>
        </div>
      )}
    </div>
  );
}
